import { PAGES, GROUPS, defsOfPage } from './settings-schema.ts';
import { T, type Trilingual } from './i18n.ts';
import { pageCounts, planPage } from './card-plan.ts';

/**
 * 页面的前端补充信息。
 *
 * 后端清单里的 `PAGES` 只有 id、分组、标题 —— 那是它该管的部分。
 * 图标、哪些页面还没做、每页欠着什么，这些是界面的事，放这里。
 */

export { PAGES, GROUPS };

/** 页面图标。名字对应 components/Icon.tsx 认得的那些 */
export const PAGE_ICON: Readonly<Record<string, string>> = {
  home: 'gauge',
  inbox: 'inbox',
  rules: 'shield',
  wallet: 'wallet',
  tokenwatch: 'chart',
  chain: 'flame',
  reminders: 'clock',
  memo: 'note',
  general: 'cog',
  system: 'stetho',
  look: 'palette',
  billing: 'wallet',
  referral: 'users',
  ext: 'puzzle'
};

/**
 * 手机底栏默认放哪 5 个。用户在外观页「导航」里可以自己挑（存在布局里，跟着账号走）；
 * 没挑满 5 个时用这几个补上。
 */
export const TAB_DEFAULTS: readonly string[] = ['home', 'rules', 'wallet', 'reminders', 'look'];

/**
 * 清单里一个配置项都没有的页面。
 *
 * 原型里这几页整页都是定制界面（总览的看板、收件箱的告警流、
 * 链热度的排行、扩展预览），所以生成脚本一项都没收 ——
 * 它们不是「配置」，是「展示」。
 *
 * 这几页对应的后端接口**还不存在**，所以网页版如实显示「还没做」。
 * 摆一屏假数字是这个产品里最危险的做法：用户会拿它当实况去决定加不加仓。
 *
 * **收件箱已经从这里搬走了**（搬进下面的 DATA_PAGES）：
 * 它要的数据一直都在 `notify_outbox` 里躺着，现在有了 `GET /api/inbox`
 * 和一个真页面 —— 每一条提醒往哪几个通道发了、到了没到、没到是为什么。
 */
export const CUSTOM_PAGES: ReadonlySet<string> = new Set([]);

/**
 * 有真实数据界面（不只是设置）的页面。
 *
 * `home` 在这里而不在 CUSTOM_PAGES 里：它的数字全部来自 `/api/overview`，
 * 一个假的都没有。还差的部分（盈亏曲线、告警流）在页面底部如实列着。
 *
 * `inbox` 同理：它读 `/api/inbox`（发件箱的真实投递记录），
 * 一个数字都不是编的。游客看到的那四条标着「示例」。
 */
export const DATA_PAGES: ReadonlySet<string> = new Set(['wallet', 'home', 'tokenwatch', 'reminders', 'billing', 'inbox', 'memo', 'referral', 'ext', 'chain']);

/**
 * 这些页面配的东西靠**价格轮询**执行，而不是（只靠）纪律判定。
 *
 * ════════════════════════════════════════════════════════
 * 这个集合原来叫 NOT_POLLED_PAGES，语义是「我们还没建轮询」。
 * ════════════════════════════════════════════════════════
 * 那是一条硬编码的、给用户看的警告，守的是一个真的差点发生的静默谎言：
 * 价格监控那一页原来靠「判定引擎没在跑」兜着，调度器一上线那条提示
 * 就消失了 —— 而价格轮询是另一件事。于是那一页会在「调度器活着」的
 * 时候告诉用户一切正常，而他配的价格告警一条都不会发。
 *
 * **轮询现在建好了**（`packages/scheduler` 的第二个租约 `price`），
 * 所以这个集合的语义跟着变了：从「我们还没建」变成
 * 「这一页要另外看价格轮询那一路活着没」。
 *
 * 为什么不干脆删掉：两路是两个独立的租约，可以一个活一个死。
 * 纪律判定活着不等于价格告警会响 —— 那两件事必须分开问、分开说。
 */
export const PRICE_DRIVEN_PAGES: ReadonlySet<string> = new Set(['tokenwatch']);

/**
 * 每一页还欠着什么。
 *
 * 这份清单是给用户看的，也是给我们自己看的 ——
 * 界面上如实列出来，比记在文档里更难忘掉。
 */
export const PAGE_GAPS: Readonly<Record<string, readonly Trilingual[]>> = {
  /* 今日盈亏、连亏、期望值、在座、最近平仓都接了真数据。剩下这些还没做 */
  /* home 的「当前持仓」2026-10-30 做了（pages/HoldingsCard.tsx，扩展填了 GMGN 签名私钥才有数据），从这里拿掉了 */
  /* 投递流水（每一条提醒到了没到、没到是为什么）做完了，
     所以原来那两条欠账从这里删掉了 —— 留着就是反过来撒谎。
     剩下这几条是真的还没做 */
  inbox: [
    T('相关成交明细（发件箱里没存成交 id，要先把事件和成交关联起来）',
      '相關成交明細（發件箱裏沒存成交 id，要先把事件和成交關聯起來）',
      'Related trades (the outbox does not store trade ids yet)'),
    T('「当时为什么下这一单」归因（那是一个独立的功能，不属于这一页）',
      '「當時為什麼下這一單」歸因（那是一個獨立的功能，不屬於這一頁）',
      'Trade attribution (a separate feature, not part of this page)')
    /* 键盘流、铃铛角标、这一页上的「确认 / 延后 / 已处理」2026-10-29 做了，从这里删掉 */
  ],
  /* 2026-10-29 做了第一版（DexScreener）和第二阶段（DefiLlama + 三种预警），见 @jyfk/heat。下面是真的还没做的 */
  /* 2026-10-30：这里原来列了两条，都不是「没做完」—— 一条是老板 2026-10-29 自己定的「升到超热不推送」（写在页面上那一行说明里），
     一条是「新池子速度、独立买家人数」只有 GeckoTerminal 付费版能定时取（老板 2026-10-30 定了先不接：DexScreener + DefiLlama 两个免费源够用；页面的数据来源说明里如实写着）。
     列成「还欠」会让人以为做到一半，所以拿掉 */
  chain: [],

  /* 扩展状态面板做了（2026-10-29，pages/ExtPage.tsx）。「桌面窗口预览」是桌面版的东西：
     桌面版会在屏幕上弹自己的窗口（熔断遮罩、提醒弹窗），网页版没有那种窗口 —— 提醒都推到手机上 */
  /* 「怎么安装」2026-10-31 不再是欠账：老板上架后在运维后台填商店链接，扩展状态页自己出现安装按钮（lib/ext-store.ts） */
  ext: [],
  /* 代币列表、告警规则、常驻轮询三条都做完了（`price` 租约那一路），
     所以那三条欠账从这里删掉了。剩下这一条是真的还没做。 */
  /* 2026-10-28：多条规则、「立即检查一轮」都做了；「发一条测试通知」早就在页头了（那一句原来写错了）。
     这一页没有还欠的东西了，所以不列 —— 留一个空数组页脚就不画 */
  tokenwatch: [],
  /* 事项、到点触发、催办、一键处理链接都做完了（租约 remind 那一路）。
     弹窗那一组（样式、全屏、提示音、语音）、邮件、Bark 细调、测试按钮都在各自的卡片上
     标着「还没做」，不在这里重复 */
  reminders: [],
  /* 备忘录两张卡、系统页的自检 / 检测 / 执行记录、邀请页那六张、交易纪律第 12 条、
     外观页的自定义图标：现在都按原型的卡片结构画在原位，卡片上标着「还没做」，
     所以从这份页脚清单里删掉了（两处都写只会让人以为欠了两份）。
     这里只留**没有对应卡片**的欠账 */
  /* 设置变更记录、「它在正常保护我吗」做了（2026-10-29，pages/SystemCards.tsx）。
     自检 / 全面检测 / 一键修复 / 执行记录那几张卡在原位标着「还没做」，不在这里重复 */
  system: [],
  /* 套餐对比、下单、唯一金额、二维码、订单列表都做完了（租约 pay 那一路扫链）。
     剩下这两条是真的还没做 */
  billing: [
    T('发票与收据（现在只有订单记录）', '發票與收據（現在只有訂單記錄）', 'Invoices and receipts'),
    T('自助退款和换链（转错链、转错金额的，在那张订单上点「我付了，没开通」，我们人工处理）',
      '自助退款和換鏈（轉錯鏈、轉錯金額的，在那張訂單上點「我付了，沒開通」，我們人工處理）',
      'Self-service refunds and chain switches (for now, press "I paid, not activated" on the order and we handle it by hand)')
  ],
  /* 邀请关系已经接上了（带 ?ref= 进来 → 存本地 → 注册成功后调 /api/referral/bind，
     见 lib/referral-bind.ts；接口那边还拦了「注册超过 7 天就不能再绑」）。
     六张返佣明细的卡还在原位标着「还没做」，那是卡片上看得出来的，不在这份页脚清单里重复 */
  referral: [],
  rules: [],
  general: [],
  look: []
};

const COUNT_CACHE = new Map<string, number>();

/**
 * 这一页画出来几项设置。侧栏上显示，**和那一页页头说的是同一个数**。
 *
 * 以前数的是清单里这一页的全部键（交易纪律 116），而页面上真画出来的是 99 ——
 * 藏掉的表单卡、不收的凭据、桌面版的连接参数都不画。两个数对不上，
 * 用户会以为少显示了什么。清单和卡片结构都是静态的，算一次记下来。
 */
export function settingCountOf(page: string): number {
  let n = COUNT_CACHE.get(page);
  if (n === undefined) {
    n = pageCounts(planPage(page)).inputs;
    COUNT_CACHE.set(page, n);
  }
  return n;
}

/** 这一页清单里的全部键（含不画的）。页面分类的测试用 */
export function definedCountOf(page: string): number {
  return defsOfPage(page).length;
}

export function pageTitle(page: string): Trilingual | null {
  const p = PAGES.find(x => x.id === page);
  return p ? p.title : null;
}
